import { memo, useState } from 'react';
import { Link } from 'react-router-dom';
import { assetFileName, RENDERED_SLIDES_BUCKET } from '../../lib/storage';
import type { SlideBackground } from '../../types/api';
import { AssetPreview } from '../knowledge-base/AssetPreview';
import { assetsApi } from '../knowledge-base/knowledgeBaseApi';
import { knowledgeBaseSectionPath } from '../knowledge-base/sections';
import { useBrandColors, type BrandColor } from '../knowledge-base/useBrandColors';
import { useCollection } from '../knowledge-base/useCollection';
import {
  SLIDE_PATTERN_LABELS,
  SLIDE_PATTERNS,
  patternBackgroundCss,
  pixelsOf,
} from './slideSources';

type BackgroundKind = SlideBackground['kind'];

interface BackgroundTab {
  kind: BackgroundKind;
  label: string;
}

const OWNED_TABS: readonly BackgroundTab[] = [
  { kind: 'color', label: 'Colour' },
  { kind: 'gradient', label: 'Gradient' },
  { kind: 'pattern', label: 'Pattern' },
  { kind: 'asset', label: 'Image' },
];

/** Only offered when this run actually produced backdrops. */
const GENERATED_TAB: BackgroundTab = { kind: 'generated', label: 'Generated' };

/** Case-insensitive hex compare — the API and the swatch may differ in case. */
function sameColor(left: string, right: string): boolean {
  return left.trim().toLowerCase() === right.trim().toLowerCase();
}

/**
 * Gradient presets, built from the brand colors themselves.
 *
 * Every ordered pair of two different brand colors, so the list grows with the
 * knowledge base and can never contain a colour the brand does not own.
 */
function gradientPresets(colors: BrandColor[]): { from: BrandColor; to: BrandColor }[] {
  return colorPairs(colors).map(([from, to]) => ({ from, to }));
}

/** Every ordered pair of two different brand colours: the one source of both preset lists. */
function colorPairs(colors: BrandColor[]): [BrandColor, BrandColor][] {
  const pairs: [BrandColor, BrandColor][] = [];
  for (const first of colors) {
    for (const second of colors) {
      if (first.name !== second.name) pairs.push([first, second]);
    }
  }
  return pairs;
}

/**
 * Pattern presets, built exactly like the gradient ones: every ordered pair
 * of two different brand colours, as ground and ink. The server refuses any
 * other colour on a pattern, so the editor never offers one.
 */
function patternPresets(colors: BrandColor[]): { ground: BrandColor; ink: BrandColor }[] {
  return colorPairs(colors).map(([ground, ink]) => ({ ground, ink }));
}

/** A swatch chip previews the tile as if the slide were this many pixels wide. */
const PATTERN_CHIP_CANVAS = 360;

interface SlideBackgroundPickerProps {
  businessId: string;
  /**
   * AI backdrops this run generated, as `rendered-slides` keys. The server only
   * accepts a `generated` background whose path is on this list.
   */
  backdrops: string[];
  value: SlideBackground;
  onChange: (next: SlideBackground) => void;
  disabled?: boolean;
}

/**
 * The background half of the constrained layer editor.
 *
 * One tab per arm of `SlideBackground`, and every option on them already
 * exists: a brand colour, a gradient between two brand colours, a repeat
 * pattern in one brand colour over another (a type-only slide), an uploaded
 * asset, or a backdrop this pipeline run drew. There is no colour field, no
 * upload control and — pointedly — no "generate another backdrop" button. This
 * screen picks, it does not commission: new art comes from a new run, which is
 * what the reject loop is for. Uploading belongs to the knowledge base, which
 * is linked from the empty states.
 *
 * Memoised: it holds two collections of its own and rebuilds every gradient
 * pair, and none of that has any business re-running because a layer was
 * dragged a centimetre on the stage beside it.
 */
export const SlideBackgroundPicker = memo(function SlideBackgroundPicker({
  businessId,
  backdrops,
  value,
  onChange,
  disabled = false,
}: SlideBackgroundPickerProps) {
  const hasBackdrops = backdrops.length > 0;
  const [tab, setTab] = useState<BackgroundKind>(() =>
    value.kind === 'generated' && !hasBackdrops ? 'color' : value.kind,
  );
  const { colors, isLoading: isLoadingColors, error: colorsError } = useBrandColors(businessId);
  const assets = useCollection(assetsApi, businessId);
  const presets = gradientPresets(colors);
  const patternPairs = patternPresets(colors);
  const tabs = hasBackdrops ? [...OWNED_TABS, GENERATED_TAB] : OWNED_TABS;

  return (
    <div className="bg-picker">
      <span className="field__label">Background</span>

      <div className="tabs bg-picker__tabs" role="tablist" aria-label="Background type">
        {tabs.map((option) => (
          <button
            className={tab === option.kind ? 'tabs__tab tabs__tab--active' : 'tabs__tab'}
            key={option.kind}
            type="button"
            role="tab"
            aria-selected={tab === option.kind}
            disabled={disabled}
            onClick={() => setTab(option.kind)}
          >
            {option.label}
          </button>
        ))}
      </div>

      {tab === 'color' ? (
        <div className="bg-picker__panel">
          {isLoadingColors ? (
            <p className="kb-section__status" role="status">
              Loading your brand colours
            </p>
          ) : colors.length === 0 ? (
            <p className="field__hint">
              No brand colours added yet.{' '}
              <Link to={knowledgeBaseSectionPath('brand-identity')}>
                Set them in About your brand
              </Link>{' '}
              and they show up here. The editor never invents a colour.
            </p>
          ) : (
            <ul className="bg-swatches">
              {colors.map((color) => {
                const isSelected = value.kind === 'color' && sameColor(value.color, color.value);
                return (
                  <li key={color.name}>
                    <button
                      className={isSelected ? 'bg-swatch bg-swatch--selected' : 'bg-swatch'}
                      type="button"
                      aria-pressed={isSelected}
                      disabled={disabled}
                      onClick={() => onChange({ kind: 'color', color: color.value })}
                    >
                      <span
                        className="bg-swatch__chip"
                        style={{ background: color.value }}
                        aria-hidden="true"
                      />
                      <span className="bg-swatch__name">{color.name}</span>
                      <span className="bg-swatch__value">{color.value}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {colorsError ? <p className="field__hint">{colorsError}</p> : null}
        </div>
      ) : null}

      {tab === 'gradient' ? (
        <div className="bg-picker__panel">
          {presets.length === 0 ? (
            <p className="field__hint">
              A gradient needs two brand colours.{' '}
              <Link to={knowledgeBaseSectionPath('brand-identity')}>Add another one</Link> and the
              presets appear.
            </p>
          ) : (
            <ul className="bg-swatches">
              {presets.map((preset) => {
                const isSelected =
                  value.kind === 'gradient' &&
                  sameColor(value.from, preset.from.value) &&
                  sameColor(value.to, preset.to.value);
                return (
                  <li key={`${preset.from.name}-${preset.to.name}`}>
                    <button
                      className={isSelected ? 'bg-swatch bg-swatch--selected' : 'bg-swatch'}
                      type="button"
                      aria-pressed={isSelected}
                      disabled={disabled}
                      onClick={() =>
                        onChange({
                          kind: 'gradient',
                          from: preset.from.value,
                          to: preset.to.value,
                        })
                      }
                    >
                      <span
                        className="bg-swatch__chip"
                        style={{
                          background: `linear-gradient(135deg, ${preset.from.value}, ${preset.to.value})`,
                        }}
                        aria-hidden="true"
                      />
                      <span className="bg-swatch__name">
                        {preset.from.name} → {preset.to.name}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      ) : null}

      {tab === 'pattern' ? (
        <div className="bg-picker__panel">
          {patternPairs.length === 0 ? (
            <p className="field__hint">
              A pattern needs two brand colours, one for the ground and one for the lines.{' '}
              <Link to={knowledgeBaseSectionPath('brand-identity')}>Add another one</Link> and the
              presets appear.
            </p>
          ) : (
            <>
              <p className="field__hint">
                Type on a brand pattern, no picture behind it. A re-render, no AI call.
              </p>
              {SLIDE_PATTERNS.map((pattern) => (
                <div className="bg-picker__group" key={pattern}>
                  <p className="bg-picker__group-label">{SLIDE_PATTERN_LABELS[pattern]}</p>
                  <ul className="bg-swatches">
                    {patternPairs.map((preset) => {
                      const isSelected =
                        value.kind === 'pattern' &&
                        value.pattern === pattern &&
                        sameColor(value.ground, preset.ground.value) &&
                        sameColor(value.ink, preset.ink.value);
                      return (
                        <li key={`${preset.ground.name}-${preset.ink.name}`}>
                          <button
                            className={isSelected ? 'bg-swatch bg-swatch--selected' : 'bg-swatch'}
                            type="button"
                            aria-pressed={isSelected}
                            aria-label={`${SLIDE_PATTERN_LABELS[pattern]}: ${preset.ink.name} on ${preset.ground.name}`}
                            disabled={disabled}
                            onClick={() =>
                              onChange({
                                kind: 'pattern',
                                pattern,
                                ground: preset.ground.value,
                                ink: preset.ink.value,
                              })
                            }
                          >
                            <span
                              className="bg-swatch__chip bg-swatch__chip--pattern"
                              style={patternBackgroundCss(
                                {
                                  pattern,
                                  ground: preset.ground.value,
                                  ink: preset.ink.value,
                                },
                                pixelsOf(PATTERN_CHIP_CANVAS),
                              )}
                              aria-hidden="true"
                            />
                            <span className="bg-swatch__name">
                              {preset.ink.name} on {preset.ground.name}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </>
          )}
        </div>
      ) : null}

      {tab === 'asset' ? (
        <div className="bg-picker__panel">
          {assets.isLoading ? (
            <p className="kb-section__status" role="status">
              Loading your images
            </p>
          ) : assets.items.length === 0 ? (
            <p className="field__hint">
              No images added yet.{' '}
              <Link to={knowledgeBaseSectionPath('assets')}>Upload one in the brand profile</Link>{' '}
              and it becomes available here.
            </p>
          ) : (
            <ul className="bg-assets">
              {assets.items.map((asset) => {
                const isSelected =
                  value.kind === 'asset' && value.asset_path === asset.storage_path;
                return (
                  <li key={asset.id}>
                    <button
                      className={isSelected ? 'bg-asset bg-asset--selected' : 'bg-asset'}
                      type="button"
                      aria-pressed={isSelected}
                      disabled={disabled}
                      onClick={() => onChange({ kind: 'asset', asset_path: asset.storage_path })}
                    >
                      <AssetPreview storagePath={asset.storage_path} />
                      <span className="bg-asset__name">{assetFileName(asset.storage_path)}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {assets.error ? <p className="field__hint">{assets.error}</p> : null}
        </div>
      ) : null}

      {tab === 'generated' && hasBackdrops ? (
        <div className="bg-picker__panel">
          <p className="field__hint">
            Backdrops we drew for this post. Rejecting the draft is what asks for new ones.
          </p>
          <ul className="bg-assets">
            {backdrops.map((path, position) => {
              const isSelected = value.kind === 'generated' && value.asset_path === path;
              const label = `Backdrop ${position + 1}`;
              return (
                <li key={path}>
                  <button
                    className={isSelected ? 'bg-asset bg-asset--selected' : 'bg-asset'}
                    type="button"
                    aria-pressed={isSelected}
                    disabled={disabled}
                    onClick={() => onChange({ kind: 'generated', asset_path: path })}
                  >
                    <AssetPreview
                      storagePath={path}
                      bucket={RENDERED_SLIDES_BUCKET}
                      label={label}
                    />
                    <span className="bg-asset__name">{label}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </div>
  );
});
